import { fileNameFormat, reduceObj } from "../../../common/services/Utilities";
import {
  InsuredDataValues,
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../interfaces";
import * as React from "react";
import SearchPills from "../../../containers/SearchPills";
import { Box, Divider, Typography } from "@mui/material";
import {
  textHeaderColor,
  primaryHeaderColor,
} from "../../../refdata/constants";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import MultiHeaderSummaryTable from "../../MultiHeaderTable/MultiHeaderSummaryTable";
import { selectedStatesAlpha } from "../../../refdata/FilterOptions";
import {
  transformDataForDownload,
  transformStateName,
} from "./SummaryTableUtilities";
import ShowHideButton from "../../ShowHideButton";

interface Props {
  appState: Query;
}

export default function SummaryTablesByState({ appState }: Props) {
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat("Summary_Tables_By_State_formatted")}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      footnotes: [
        appState.filters.lastYear === "1994"
          ? "*Includes 18 conservatorships"
          : null,
      ].filter(Boolean) as string[],
    },
  ];

  const [downloadData, setDownloadData] = React.useState<any>();
  const [loading, setLoading] = React.useState(false);
  const [stateDataList, setStateDataList] = React.useState<{
    [key: string]: InsuredDataValues;
  }>({});
  const [previousYear, setPreviousYear] = React.useState(
    appState.filters.lastYear,
  );
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [totalResults, setTotalResults] = React.useState<KeyValue>({});
  const stateValues: string[] = selectedStatesAlpha.map((state) => state.value);

  const headingTitle = (
    <Box
      sx={{
        marginTop: -1,
        display: "flex",
        alignItems: "end",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        National Totals by States & Territories
      </Typography>
      <ApiDataDownload
        app="SOD"
        apiData={apiData}
        downloadData={downloadData}
      />
    </Box>
  );

  let tableData = reduceObj(
    stateValues.map((state: string) => {
      return {
        key: state,
        value: {
          all: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
          CB: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
          SA: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
          IB: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
        },
      };
    }),
  );

  React.useEffect(() => {
    if (
      (appState.filters.lastYear !== previousYear || initialLoad) &&
      appState.ui.reportType
    ) {
      setLoading(true);
      initialLoad && setInitialLoad(false);
      setPreviousYear(appState.filters.lastYear);
      let isMounted = true;
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&get_unique=CERT&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:CB`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&get_unique=CERT&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&get_unique=CERT&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:IB`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=STALPBR&get_unique=CERT&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            }`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=INSURED&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            }`,
          ),
        ])
          .then((response) => {
            if (isMounted) {
              const processResponseData = (
                responseData: KeyValue[],
                keyPrefix: string,
                targetProperty: "all" | "CB" | "SA" | "IB",
              ) => {
                responseData.forEach((data: KeyValue) => {
                  const state: string = data.STALPBR;
                  if (state && tableData[state]) {
                    tableData[state][keyPrefix + targetProperty] = {
                      unique_count: data.unique_count ?? 0,
                      sum_DEPSUMBR: data.sum_DEPSUMBR ?? 0,
                      count: data.count ?? 0,
                    };
                  }
                });
              };
              response[0].data &&
                processResponseData(response[0].data, "", "CB");
              response[1].data &&
                processResponseData(response[1].data, "", "SA");
              response[2].data &&
                processResponseData(response[2].data, "", "IB");
              processResponseData(response[3].data, "", "all");
              let resultTotals: KeyValue = {};
              response[4].data.forEach((data: KeyValue) => {
                resultTotals[data["INSURED"]] = {
                  unique_count: data.sum_BKMO,
                  sum_DEPSUMBR: data.sum_DEPSUMBR,
                  count: data.count,
                };
              });
              const all = {
                all: {
                  unique_count: response[4].totals?.sum_BKMO,
                  sum_DEPSUMBR: response[4].totals?.sum_DEPSUMBR,
                  count: response[4].totals?.count,
                },
              };
              resultTotals = { ...resultTotals, ...all };
              const totals = {
                "Totals / Unique Institutions": resultTotals,
              };
              const tableDataWithTotals = { ...tableData, ...totals };
              const resultForDownload = transformDataForDownload(
                tableDataWithTotals,
                `06/30/${appState.filters.lastYear}`,
                "State",
              );
              setDownloadData(resultForDownload);
              setStateDataList(tableData);
              setTotalResults(resultTotals);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [appState.filters, initialLoad, previousYear, appState.ui, tableData]);

  const stateDataMap: KeyValue = {};
  let key = 0;
  for (const value of stateValues) {
    stateDataMap[key] = transformStateName(value);
    key = key + 1;
  }

  const stateHeader: TableHeader[] = [
    {
      title: "State",
      styles: {
        backgroundColor: primaryHeaderColor,
        color: textHeaderColor,
        textAlign: "left",
      },
      colSpan: 3,
      apiField: "",
    },
  ];

  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      <MultiHeaderSummaryTable
        tableData={stateDataList}
        totalsData={totalResults}
        firstColumnData={stateDataMap}
        firstHeader={stateHeader}
        showAsterisks={false}
        loading={loading}
        tableType={"stalpbr"}
        lastYear={appState.filters.lastYear}
      />
      {appState.filters.lastYear === "1994" && (
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 900,
            minWidth: 900,
            fontSize: 16,
            fontWeight: "bold",
          }}
        >
          *Includes 18 conservatorships
        </Typography>
      )}
    </>
  );
}
